import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaBookOpen, FaWrench, FaGraduationCap, FaLeaf, FaHandshake, FaShoppingBag, FaArrowRight, FaCheck, FaPhoneAlt, FaSearch, FaRulerCombined, FaSeedling } from 'react-icons/fa';
import PlantBackground from '../components/PlantBackground';
import './Services.css';

const services = [
  {
    id: 'plant-library',
    icon: <FaBookOpen size={28} />,
    title: 'Plant Library',
    subtitle: 'Borrow, Exchange & Discover Plants',
    desc: 'Nisarg Nursery Plant Library is a unique green initiative that allows plant lovers, schools, offices, and communities to borrow, exchange, and explore a wide variety of indoor and outdoor plants. Promote sustainable gardening while learning about plant care and biodiversity.',
    features: [
      '500+ Plant Varieties',
      'Plant Exchange Program',
      'Educational Plant Resources',
      'Sustainable Gardening Initiative',
      'Community Green Learning'
    ],
    image: '/images/garden.jpg',
    color: '#2e7d32',
    path: '/services/plant-library',
  },
  {
    id: 'amc-maintenance',
    icon: <FaWrench size={28} />,
    title: 'AMC Plant Maintenance',
    subtitle: 'Professional Garden Care Solutions',
    desc: 'Our Annual Maintenance Contract (AMC) ensures your indoor plants, outdoor landscapes, gardens, and green spaces remain healthy, attractive, and professionally maintained throughout the year by experienced horticulture experts.',
    features: [
      'Site Visits',
      'Pruning & Trimming',
      'Plant Nutrition Management',
      'Pest & Disease Control',
      'Monthly Health Monitoring'
    ],
    image: '/images/hanging6.jpg',
    color: '#1b5e20',
    path: '/services/amc-maintenance',
  },
  {
    id: 'training-programs',
    icon: <FaGraduationCap size={28} />,
    title: 'Gardening & Horticulture Training',
    subtitle: 'Learn from Plant Experts',
    desc: 'Gain practical knowledge of gardening, plant care, nursery management, organic farming, landscaping, and sustainable horticulture through expert-led workshops and professional training programs.',
    features: [
      'Beginner to Advanced Training',
      'Hands-On Practical Sessions',
      'Home Gardening Guidance',
      'Professional Horticulture Skills',
      'Certificate Programs'
    ],
    image: '/images/Dumb Canes.jpg',
    color: '#388e3c',
    path: '/services/training-programs',
  },
  {
    id: 'plant-rental',
    icon: <FaLeaf size={28} />,
    title: 'Plant Rental Service',
    subtitle: 'Green Solutions for Events & Workspaces',
    desc: 'Enhance offices, exhibitions, weddings, hotels, corporate events, and commercial spaces with premium plant rental services. We provide delivery, installation, maintenance, and collection for a hassle-free experience.',
    features: [
      'Office Plant Rentals',
      'Event Green Decor',
      'Indoor & Outdoor Plants',
      'Installation & Maintenance',
      'Flexible Rental Packages'
    ],
    image: '/images/limbu.jpg',
    color: '#4caf50',
    path: '/services/plant-rental',
  },
  {
    id: 'franchise-model',
    icon: <FaHandshake size={28} />,
    title: 'Nursery Franchise Program',
    subtitle: 'Grow Your Green Business',
    desc: 'Become a part of the Nisarg Nursery network and establish your own successful nursery business with expert training, plant sourcing support, branding assistance, operational guidance, and marketing support.',
    features: [
      'Business Setup Support',
      'Branding Assistance',
      'Plant Supply Network',
      'Marketing Support',
      'Ongoing Expert Guidance'
    ],
    image: '/images/black pepper.jpg',
    color: '#66bb6a',
    path: '/services/franchise-model',
  },
  {
    id: 'plant-care-products',
    icon: <FaShoppingBag size={28} />,
    title: 'Plant Care Products',
    subtitle: 'Everything Your Plants Need',
    desc: 'Shop premium quality plant food, organic fertilizers, cocopeat, decorative pots, moisture retention solutions, garden essentials, and plant growth products designed for healthy and sustainable plant development.',
    features: [
      'Organic Plant Food',
      'Decorative Pots & Planters',
      'Premium Soil Mixes',
      'Moisture Retention Products',
      'Growth Enhancers'
    ],
    image: '/images/Aloe Vera.jpg',
    color: '#81c784',
    path: '/services/plant-store',
  },
];

const ServiceCard = ({ service, isActive, onClick }) => {
  const isMobile = window.innerWidth <= 640;

  return (
    <div
      className={`service-card service-card-${service.id} ${isActive ? 'active' : ''}`}
      onClick={onClick}
      id={service.id}
    >
      <div className="service-card-header">
        <div className="service-icon">{service.icon}</div>

        <div className="service-card-text">
          <h3 className="service-title">{service.title}</h3>
          <p className="service-subtitle">{service.subtitle}</p>
        </div>

        <div className="service-arrow">
          <FaArrowRight />
        </div>
      </div>

      {isActive && (
        <div className="service-card-body">

          <p className="service-desc">{service.desc}</p>

          <ul className="service-features">
            {service.features.map(f => (
              <li key={f} className="service-feature-item">
                <span className="check-icon"><FaCheck /></span>
                {f}
              </li>
            ))}
          </ul>

          {/* MOBILE IMAGE */}
          {isMobile && (
            <div className="mobile-service-image">
              <img
                src={service.image}
                alt={service.title}
                className="mobile-service-img"
              />
            </div>
          )}

          <div className="service-card-footer">
            <Link
              to={service.path}
              className="btn-primary"
              style={{ padding: '10px 22px', fontSize: '0.88rem' }}
            >
              <span>Explore Now</span>
              <FaArrowRight />
            </Link>
          </div>

        </div>
      )}
    </div>
  );
};

const Services = () => {
  const [activeService, setActiveService] = useState(0);

  return (
    <div className="page-wrapper services-page">
      {/* ════ SERVICES HERO — Icon Showcase ════ */}
      <section className="services-hero">
        <div className="services-hero-content">

          <div className="services-left">

            <div className="section-tag-minimal">
              PROFESSIONAL NURSERY • LANDSCAPING • PLANT CARE
            </div>

            <h1 className="services-title">
              Professional Nursery &
              <span className="gold-text"> Landscape<br />Solutions </span><br />
              for Modern Spaces
            </h1>

            <p className="services-desc">
              Nisarg Nursery offers professional landscaping services, plant rental solutions, garden maintenance, plant library programs, horticulture training, and premium plant care support. We help create healthy, beautiful, and sustainable green spaces for homes, offices, institutions, resorts, and commercial properties.
            </p>

            <div className="services-btns">
              <button className="btn-primary">
                Explore Services
              </button>

            </div>

          </div>

          <div className="services-center">
            <img
              src="/images/Golden Pothos1.jpg"
              alt="Landscape Services"
            />
          </div>

          <div className="services-right">

            <div className="floating-card">
              🌿 Landscape Design
            </div>

            <div className="floating-card">
              🪴 Plant Rental
            </div>

            <div className="floating-card">
              🏡 Garden Maintenance
            </div>

            <div className="floating-card">
              🌱 Indoor Plants
            </div>

          </div>

          <h2 className="services-hero-title">
            Plant & <span>Landscape Solutions</span>
          </h2>

        </div>
      </section>

      {/* ════ SERVICES SHOWCASE ════ */}
      <section className="services-showcase">
        <div className="services-container">
          {/* Left: Accordion */}
          <div className="services-accordion">
            {services.map((s, i) => (
              <ServiceCard
                key={s.id}
                service={s}
                isActive={activeService === i}
                onClick={() => setActiveService(i)}
              />
            ))}
          </div>

          {/* Right: Visual */}
          <div className="services-visual">
            <div className="services-img-wrap">
              <img
                src={services[activeService].image}
                alt={services[activeService].title}
                className="services-img"
                key={activeService}
              />
              <div className="services-img-overlay" />
            </div>
            <div className="services-visual-badge">
              <div className="svg-badge-icon">{services[activeService].icon}</div>
              <div>
                <div className="svg-badge-title">{services[activeService].title}</div>
                <div className="svg-badge-price">{services[activeService].subtitle}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════ PROCESS SECTION ════ */}
      <section className="process-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-tag"> Complete Plant & Landscape Solutions</div>
            <h2 className="section-title">Our Professional <span className="gold-text">Service Process</span></h2>
            <p className="section-subtitle">
              From consultation and site assessment to installation and ongoing maintenance, our experienced nursery and landscaping team ensures a smooth, reliable, and result-oriented service experience.
            </p>
          </div>
          <div className="process-steps">
            {[
              { step: '01', icon: <FaPhoneAlt size={22} />, title: 'Get in Touch', desc: 'Call, WhatsApp or fill our contact form. We respond within 24 hours.' },
              { step: '02', icon: <FaSearch size={22} />, title: 'Site Assessment', desc: 'Our experts visit your space for a detailed analysis and understanding.' },
              { step: '03', icon: <FaRulerCombined size={22} />, title: 'Custom Proposal', desc: 'We create a tailored plan, design and transparent quote for you.' },
              { step: '04', icon: <FaSeedling size={22} />, title: 'Execution & Care', desc: 'We deliver, install and provide ongoing care for lasting results.' },
            ].map(({ step, icon, title, desc }) => (
              <div key={step} className="process-step">
                <div className="step-number">{step}</div>
                <div className="step-icon">{icon}</div>
                <h3 className="step-title">{title}</h3>
                <p className="step-desc">{desc}</p>
                <div className="step-connector" />
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA */}
      <section className="services-cta">
        <div className="cta-inner">
          <div className="cta-glow" />
          <div className="section-tag" style={{ justifyContent: 'center' }}>🌿 Need Expert Guidance For Your Green Space?</div>
          <h2 className="cta-title">Not Sure Which Service <span className="gold-text">Fits You?</span></h2>
          <p className="cta-desc">Whether you need landscaping, plant rental, garden maintenance, horticulture training, or plant care solutions, our nursery experts are ready to help you choose the right service for your space and budget.</p>
          <div className="cta-btns">
            <Link to="/contact" className="btn-primary">
              <span>Book Free Consultation</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
